import React, { useEffect, useMemo, useRef } from 'react';

import { AttentionMessageProps } from '@eika/attention-message';
import { ButtonTypes } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { RadioButtonPanels } from '@eika/radio-button';

import useTranslation from '../../../../hooks/useTranslation';
import { eFakturaTrackingIDs } from '../../../../utils/gtm/trackIDs/eFaktura';
import { AmountSelectedType } from '../../form/state';
import useFormContext from '../../form/useFormContext';
import AmountSelectorsSkeletons from './AmountSelectorsSkeletons';

type Props = {
    validationError?: string;
    setValidationError?: (value: string | undefined) => void;
    value?: AmountSelectedType;
    onChange: (value: AmountSelectedType) => void;
    resetSelectedArray: () => void;
    locked?: boolean;
};

const AmountSelectors: React.FC<Props> = ({
    setValidationError,
    validationError,
    onChange,
    value,
    resetSelectedArray,
    locked = false,
}) => {
    const changeFunc = useRef(onChange);
    const resetArrayFunc = useRef(resetSelectedArray);

    const translated = useTranslation();
    const {
        creditors: { error, isLoading, refetch, data },
    } = useFormContext();

    const showAttentionMessage = useMemo(() => !isLoading && (data.length === 0 || !!error), [isLoading, data, error]);

    const message = useMemo((): AttentionMessageProps | undefined => {
        if (!showAttentionMessage) {
            return undefined;
        }
        if (error) {
            return {
                type: 'error',
                clickableItem: (
                    <ButtonWithSpinner
                        showSpinner={isLoading}
                        onClick={refetch}
                        buttonType={ButtonTypes.STANDARD_POSITIVE}
                    >
                        {translated.tryAgain}
                    </ButtonWithSpinner>
                ),
                text: translated.eFakturaSharing.errorFetchingEfaktura,
            };
        }

        if (data.length === 0) {
            return { text: translated.eFakturaSharing.noEfakturas };
        }

        return undefined;
    }, [showAttentionMessage, error, isLoading, refetch, data, translated.eFakturaSharing, translated.tryAgain]);

    useEffect(() => {
        if (showAttentionMessage) {
            changeFunc.current('all');
            resetArrayFunc.current();
        }
    }, [showAttentionMessage, resetArrayFunc, changeFunc]);

    return isLoading ? (
        <AmountSelectorsSkeletons />
    ) : (
        <RadioButtonPanels
            label={translated.eFakturaSharing.howManyEfakturasToShare}
            onChange={(value) => {
                onChange(value as AmountSelectedType);
                resetSelectedArray();
                setValidationError?.(undefined);
            }}
            options={[
                {
                    value: 'all',
                    label: translated.eFakturaSharing.amountSelected.all,
                    attentionMessage: message,
                    disabled: locked,
                },
                {
                    value: 'some',
                    label: translated.eFakturaSharing.amountSelected.some,
                    disabled: locked || showAttentionMessage,
                },
            ]}
            selectedOption={value}
            errorMessage={validationError}
            dataTrackId={eFakturaTrackingIDs.shareEFaktura.id}
        />
    );
};

export default AmountSelectors;
